/**
 * Famílias de produtos que normalmente saem juntos no mesmo lote.
 * Usado para nomear cards unificados de forma legível, em vez do
 * genérico "UNIFICADO LOTE X (N produtos)".
 *
 * Regras:
 *  - Se TODOS os produtos do card unificado pertencem à MESMA família, usa o
 *    nome da família.
 *  - Se a família tem `nomesExatos` e algum esperado está faltando, mostra
 *    apenas os presentes (ex.: "RACK LEGACY 1.6 + 2.0" quando o 1.8 falta).
 *  - Se a família tem `prefixo`, mostra "PREFIXO suf1 + suf2" com os sufixos
 *    presentes (ex.: "BERLIN HOME 1.6 + 2.0").
 *  - Se nenhum produto casa com nenhuma família, concatena os nomes.
 */

/** Normaliza nome removendo material (MDF/Aglo) e espessura para casar
 *  "MDF RACK ARUBA" com "RACK ARUBA". */
export function nomeProdutoLimpo(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\bBERLIM\b/g, 'BERLIN')
    .replace(/\bMDF\b/g, '')
    .replace(/\bAGLOMERADO\b/g, '')
    .replace(/\bAGLO\b/g, '')
    .replace(/\b\d+\s*MM\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Produtos que NÃO devem entrar no sistema de famílias. Cada item tem:
 *  - `pattern`: regex aplicada ao nome (em uppercase, sem acentos).
 *  - `chave`: função que devolve a chave de agrupamento — pode preservar
 *    informações que `nomeProdutoLimpo` retiraria (ex.: material). */
const PRODUTOS_NAO_AGRUPAR: Array<{
  pattern: RegExp;
  chave: (nomeUpper: string) => string;
}> = [
  // RACK LEGACY 1.8 NUNCA agrupa com os outros RACK LEGACY (saída diferente)
  {
    pattern: /\bRACK\s+LEGACY\s+1[.,]?\s*8\b/,
    chave: () => 'RACK LEGACY 1.8',
  },
  // PAINEL STUDIO 2.18 é produto distinto do PAINEL STUDIO (não agrupar)
  {
    pattern: /\bPAINEL\s+STUDIO\s+2[.,]?\s*18\b/,
    chave: () => 'PAINEL STUDIO 2.18',
  },
  // PAINEL STUDIO: separa por MATERIAL (MDF vs AGLO) — não agrupar
  {
    pattern: /\bPAINEL\s+STUDIO\b/,
    chave: (n) => {
      const temMdf = /\bMDF\b/.test(n);
      const temAglo = /\bAGLOMERADO\b/.test(n) || /\bAGLO\b/.test(n);
      const mat = temMdf ? 'MDF' : temAglo ? 'AGLO' : 'SEM MATERIAL';
      return `PAINEL STUDIO ${mat}`;
    },
  },
];

function nomeUpperSemAcento(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

type Familia =
  | {
      /** Nome curto e legível usado quando a família está completa. */
      nome: string;
      /** Conjunto de nomes "completos" esperados. Card só casa se TODOS os
       *  produtos do card estiverem nessa lista. */
      nomesExatos: string[];
    }
  | {
      nome: string;
      /** Prefixo. Card só casa se TODOS os produtos começarem com ele.
       *  O sufixo de cada nome (ex.: "1.6", "HOME 1.8") é usado para indicar
       *  variantes quando elas aparecem. */
      prefixo: string;
    }
  | {
      nome: string;
      /** Token livre. Card casa se TODOS os produtos contiverem este token
       *  (ex.: "BERLIN" cobre "BERLIN HOME", "BERLIN ECO"). */
      contemTodos: string;
    };

export const FAMILIAS_PRODUTOS: Familia[] = [
  // Famílias com nomes exatos (pares ou trios fixos que saem juntos)
  { nome: 'ARUBA + CAYMAN', nomesExatos: ['RACK ARUBA', 'RACK CAYMAN'] },
  { nome: 'KING + ANDROS', nomesExatos: ['RACK KING', 'RACK ANDROS'] },
  /** Andorra + Sierra saem juntos — variantes (1.3, 1.72…) e MDF compartilham um tempo de produção. */
  { nome: 'ANDORRA + SIERRA', nomesExatos: ['BUFFET ANDORRA', 'BUFFET SIERRA'] },

  // Asturias: rack e painel são famílias SEPARADAS no Tempo Inteligente;
  // variantes (1.8, 0.92, etc.) compartilham o mesmo tempo de produção.
  { nome: 'RACK ASTURIAS', prefixo: 'RACK ASTURIAS' },
  { nome: 'PAINEL ASTURIAS', prefixo: 'PAINEL ASTURIAS' },
  /** Aparador bar + bandeja saem juntos no lote — um tempo de produção, exato somado. */
  {
    nome: 'APARADOR + BANDEIJA ASTURIAS',
    nomesExatos: ['APARADOR BAR ASTURIAS', 'BANDEIJA ASTURIAS'],
  },

  // Famílias por token livre (qualquer produto que contenha a palavra)
  { nome: 'BERLIN', contemTodos: 'BERLIN' },
  { nome: 'OSLO', contemTodos: 'OSLO' },

  // Famílias por prefixo (variantes só diferem no sufixo)
  { nome: 'RACK LEGACY', nomesExatos: ['RACK LEGACY 1.6', 'RACK LEGACY 2.0'] },
  { nome: 'NICHO DIOR', prefixo: 'NICHO DIOR' },
  { nome: 'NICHO ASPEN', prefixo: 'NICHO ASPEN' },
  { nome: 'NICHO UNIVERSAL', prefixo: 'NICHO UNIVERSAL' },
  { nome: 'RACK COMODORO', prefixo: 'RACK COMODORO' },
  { nome: 'BUFFET LIV', prefixo: 'BUFFET LIV' },
  { nome: 'BUFFET ARON', prefixo: 'BUFFET ARON' },
  { nome: 'RACK ARON', prefixo: 'RACK ARON' },
  { nome: 'NICHO LIV', prefixo: 'NICHO LIV' },
  { nome: 'RACK VIGO', prefixo: 'RACK VIGO' },
  { nome: 'RACK LIV', prefixo: 'RACK LIV' },
  { nome: 'PAINEL LUMI', prefixo: 'PAINEL LUMI' },
  { nome: 'PAINEL AVANTI', prefixo: 'PAINEL AVANTI' },
  { nome: 'PAINEL EGEO', prefixo: 'PAINEL EGEO' },
  { nome: 'PAINEL ESTONE', prefixo: 'PAINEL ESTONE' },
  { nome: 'BUFFET ASTURIAS', prefixo: 'BUFFET ASTURIAS' },
  { nome: 'BUFFET MAGNUM', prefixo: 'BUFFET MAGNUM' },
  { nome: 'PAINEL LEGACY', prefixo: 'PAINEL LEGACY' },
  { nome: 'NICHO LUXO', prefixo: 'NICHO LUXO' },
  { nome: 'BUFFET ACORES', prefixo: 'BUFFET ACORES' },
  { nome: 'PAINEL LAUS', prefixo: 'PAINEL LAUS' },
  { nome: 'RACK VELLARO', prefixo: 'RACK VELLARO' },
  { nome: 'PAINEL BLISS', prefixo: 'PAINEL BLISS' },
  { nome: 'PAINEL MADERATTO', prefixo: 'PAINEL MADERATTO' },
  { nome: 'BUFFET MADERATTO', prefixo: 'BUFFET MADERATTO' },
  { nome: 'RACK AUSTIN', prefixo: 'RACK AUSTIN' },
  { nome: 'PAINEL MASTER', prefixo: 'PAINEL MASTER' },
  { nome: 'PAINEL ARBO', prefixo: 'PAINEL ARBO' },
  { nome: 'BUFFET ARGO', prefixo: 'BUFFET ARGO' },
];

/** Pares que saem juntos ocasionalmente — só afeta o nome do card unificado. */
export const PARES_COALIZAO_NOME: { nome: string; prefixos: string[] }[] = [
  { nome: 'BUFFET + RACK ARON', prefixos: ['BUFFET ARON', 'RACK ARON'] },
  { nome: 'PAINEL BLISS + MADERATTO', prefixos: ['PAINEL BLISS', 'PAINEL MADERATTO'] },
];

function casaPrefixoFamilia(nomeLimpo: string, prefixo: string): boolean {
  const pref = nomeProdutoLimpo(prefixo);
  return nomeLimpo === pref || nomeLimpo.startsWith(pref + ' ');
}

/** Tokens de produto usados para casar complementos MDF pelo nome (ex.: PÉ RACK ASTURIAS → RACK ASTURIAS). */
function tokensBuscaFamiliaMdf(): Array<{ token: string; familia: string }> {
  const pares: Array<{ token: string; familia: string }> = [];
  for (const fam of FAMILIAS_PRODUTOS) {
    if ('nomesExatos' in fam) {
      for (const e of fam.nomesExatos) {
        pares.push({ token: nomeProdutoLimpo(e), familia: fam.nome });
      }
    } else if ('prefixo' in fam) {
      pares.push({ token: nomeProdutoLimpo(fam.prefixo), familia: fam.nome });
    } else {
      pares.push({ token: nomeProdutoLimpo(fam.contemTodos), familia: fam.nome });
    }
  }
  for (const par of PARES_COALIZAO_NOME) {
    for (const pref of par.prefixos) {
      pares.push({ token: nomeProdutoLimpo(pref), familia: par.nome });
    }
  }
  return pares.sort((a, b) => b.token.length - a.token.length);
}

const TOKENS_BUSCA_FAMILIA_MDF = tokensBuscaFamiliaMdf();

/**
 * Família de um complemento MDF — lê o produto citado no próprio nome.
 * Ex.: "MDF PÉ RACK ASTURIAS" → RACK ASTURIAS (não PAINEL ASTURIAS do mesmo lote).
 */
export function chaveFamiliaComplementoMdf(nomeOriginal: string): string {
  const limpo = nomeProdutoLimpo(nomeOriginal);
  if (!limpo) return '';

  const direta = chaveFamiliaProduto(limpo);
  if (direta !== limpo) return direta;

  for (const { token, familia } of TOKENS_BUSCA_FAMILIA_MDF) {
    if (!token) continue;
    if (limpo === token || limpo.includes(token)) return familia;
  }

  return limpo;
}

/** Casa nome exato da família — aceita sufixo de variante (1.3, 1.72) e cabeçalho BUFFET ANDORRA/SIERRA. */
function nomeCasaExatoFamilia(nomeLimpo: string, exato: string): boolean {
  const ex = nomeProdutoLimpo(exato);
  if (!ex) return false;
  if (nomeLimpo === ex || nomeLimpo.startsWith(ex + ' ')) return true;
  const baseSemSufixo = nomeLimpo.replace(/\s+\d+(?:[.,]\d+)?$/, '').trim();
  if (!baseSemSufixo.includes('/')) return false;
  const ultimoToken = ex.split(/\s+/).pop() || '';
  return ultimoToken.length > 0 && baseSemSufixo.includes(ultimoToken);
}

/** Verifica se um nome casa com uma família. */
function nomeCasaFamilia(nomeLimpo: string, fam: Familia): boolean {
  if ('nomesExatos' in fam) {
    return fam.nomesExatos.some((e) => nomeCasaExatoFamilia(nomeLimpo, e));
  }
  if ('prefixo' in fam) {
    const pref = nomeProdutoLimpo(fam.prefixo);
    return nomeLimpo === pref || nomeLimpo.startsWith(pref + ' ');
  }
  return nomeLimpo.includes(nomeProdutoLimpo(fam.contemTodos));
}

/** Sufixo de um nome em relação a um prefixo da família. */
function sufixoVariante(nomeLimpo: string, prefixo: string): string {
  const pref = nomeProdutoLimpo(prefixo);
  if (nomeLimpo === pref) return '';
  if (nomeLimpo.startsWith(pref + ' ')) {
    return nomeLimpo.substring(pref.length).trim();
  }
  return nomeLimpo;
}

/** Recebe os nomes (originais, com MDF/AGLO/mm etc.) dos produtos do card
 *  unificado e devolve um nome inteligente. */
export function gerarNomeUnificadoInteligente(nomesOriginais: string[]): string {
  const limpos = Array.from(
    new Set(nomesOriginais.map((n) => nomeProdutoLimpo(n)).filter(Boolean))
  );
  if (limpos.length === 0) return '';
  if (limpos.length === 1) return limpos[0];

  for (const par of PARES_COALIZAO_NOME) {
    if (par.prefixos.every((pref) => limpos.some((n) => casaPrefixoFamilia(n, pref)))) {
      return par.nome;
    }
  }

  // Tenta encontrar uma família onde TODOS os nomes do card casam
  for (const fam of FAMILIAS_PRODUTOS) {
    const todosCasam = limpos.every((n) => nomeCasaFamilia(n, fam));
    if (!todosCasam) continue;

    if ('nomesExatos' in fam) {
      // Lista de exatos PRESENTES no card (em ordem da família)
      const presentes = fam.nomesExatos
        .map((e) => nomeProdutoLimpo(e))
        .filter((e) => limpos.some((n) => nomeCasaExatoFamilia(n, e)));
      // Se TODOS os esperados estão presentes => nome da família
      if (presentes.length === fam.nomesExatos.length) {
        return fam.nome;
      }
      // Faltando algum esperado => mostra os que vieram
      return presentes.join(' + ');
    }

    if ('prefixo' in fam) {
      // Coleta sufixos únicos, em ordem dos nomes encontrados
      const sufixos = Array.from(
        new Set(limpos.map((n) => sufixoVariante(n, fam.prefixo)).filter(Boolean))
      );
      if (sufixos.length === 0) return fam.nome;
      return `${fam.nome} ${sufixos.join(' + ')}`;
    }

    // contemTodos => família ampla, mantém o nome curto
    return fam.nome;
  }

  // Nenhuma família casou: concatena os nomes (no máximo 3, com +N depois)
  if (limpos.length <= 3) return limpos.join(' + ');
  return `${limpos.slice(0, 2).join(' + ')} +${limpos.length - 2}`;
}

/** Dado o nome bruto de UM produto (card individual OU unificado já renomeado),
 *  devolve a chave de família para agrupamento (ex.: RACK ASTURIAS 1.8 →
 *  "RACK ASTURIAS"; PAINEL ASTURIAS 1.8 → "PAINEL ASTURIAS"). Rack e painel
 *  Asturias são grupos distintos. Se não bater em nenhuma família, devolve o
 *  nome limpo. */
/** Extrai todos os números de lote de um texto ("8762", "8762/8763", etc.). */
export function numerosLoteDeTexto(s: string): number[] {
  const nums: number[] = [];
  const partes = String(s || '').split(
    /(?:\s+E\s+|\s*,\s*|\s*;\s*|\s*\/\s*|\s*\+\s*|\s*&\s*|\s*[-–—]\s*)/i
  );
  for (const p of partes) {
    const n = Number(String(p || '').replace(/\D/g, ''));
    if (Number.isFinite(n) && n > 0) nums.push(n);
  }
  return nums;
}

/** Todos os números de lote associados a um card (campo `lote` + originais do unificado). */
export function numerosLoteDoCard(card: {
  lote?: string;
  produtosOrigemCompleto?: Array<{ lote?: string }>;
  produtosOrigem?: Array<{ lote?: string }>;
}): number[] {
  const set = new Set<number>();
  numerosLoteDeTexto(String(card.lote || '')).forEach((n) => set.add(n));
  const originais = Array.isArray(card.produtosOrigemCompleto)
    ? card.produtosOrigemCompleto
    : Array.isArray(card.produtosOrigem)
    ? card.produtosOrigem
    : [];
  originais.forEach((o) => {
    numerosLoteDeTexto(String(o?.lote || '')).forEach((n) => set.add(n));
  });
  return Array.from(set);
}

export function chaveFamiliaProduto(nomeOriginal: string): string {
  const upper = nomeUpperSemAcento(nomeOriginal);
  // 1) Exclusões: produtos que NÃO devem entrar em família nenhuma.
  //    Mantêm sua chave própria (ex.: PAINEL STUDIO MDF ≠ PAINEL STUDIO AGLO).
  for (const ex of PRODUTOS_NAO_AGRUPAR) {
    if (ex.pattern.test(upper)) {
      return ex.chave(upper);
    }
  }
  let limpo = nomeProdutoLimpo(nomeOriginal);
  if (!limpo) return '';
  // Cadastro legado "PAINEL AVANT" → família PAINEL AVANTI
  if (/\bPAINEL\s+AVANT\b/.test(limpo) && !/\bAVANTI\b/.test(limpo)) {
    limpo = limpo.replace(/\bPAINEL\s+AVANT\b/, 'PAINEL AVANTI');
  }
  for (const fam of FAMILIAS_PRODUTOS) {
    if ('nomesExatos' in fam) {
      if (fam.nomesExatos.some((e) => nomeCasaExatoFamilia(limpo, e))) {
        return fam.nome;
      }
      // Card unificado já renomeado pode ter o NOME da família como nome
      if (nomeProdutoLimpo(fam.nome) === limpo) return fam.nome;
    } else if ('prefixo' in fam) {
      const pref = nomeProdutoLimpo(fam.prefixo);
      if (limpo === pref || limpo.startsWith(pref + ' ')) {
        return fam.nome;
      }
      if (nomeProdutoLimpo(fam.nome) === limpo) return fam.nome;
    } else {
      // contemTodos
      const token = nomeProdutoLimpo(fam.contemTodos);
      if (token && limpo.includes(token)) return fam.nome;
      if (nomeProdutoLimpo(fam.nome) === limpo) return fam.nome;
    }
  }
  return limpo;
}

/**
 * Chave de família para agrupamento no Tempo Inteligente — inclui cards que citam
 * a família no nome sem ser MDF (ex.: PÉ RACK AUSTIN → RACK AUSTIN).
 */
export function chaveFamiliaCard(nomeOriginal: string): string {
  const direta = chaveFamiliaProduto(nomeOriginal);
  const limpo = nomeProdutoLimpo(nomeOriginal);
  if (!limpo) return direta;
  if (direta !== limpo) return direta;
  const viaNome = chaveFamiliaComplementoMdf(nomeOriginal);
  return viaNome !== limpo ? viaNome : direta;
}
